import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface PriceIndexDateRangeObject {
  id: number;
  label: string;
  titleAr: string;
  titleEn: string;
  startYear: number;
  endYear: number;
  supportedIntervals: { intervalId: number; viewTypeId: number | null }[];
  intervals: number[] | string;
  index_date_range_intervals: {
    interval_id: number;
    intervals: { id: number; key: string };
  }[];
  typeId: number;
  typeName?: string;
  sort: number;
  isActive: boolean;
  isDefault: boolean;
}

export interface PriceIndexDateRangeListInfo {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  isNext: boolean;
  previous: number;
  items: Array<PriceIndexDateRangeObject>;
  errors: unknown;
}

@Module
export default class PriceIndexDateRangeModule
  extends VuexModule
  implements PriceIndexDateRangeListInfo
{
  items = [] as Array<PriceIndexDateRangeObject>;
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  previous = 0;
  isNext = true;
  errors = {};

  get priceIndexDateRangeList(): Array<PriceIndexDateRangeObject> {
    return this.items;
  }

  get priceIndexDateRangeListCount(): number {
    return this.totalItems;
  }

  @Mutation
  [Mutations.SET_PRICE_INDEX_DATE_RANGES](data) {
    this.items = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_PRICE_INDEX_DATE_RANGE_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.RESET_PRICE_INDEX_DATE_RANGES_LIST]() {
    this.items = [] as Array<PriceIndexDateRangeObject>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
  }

  @Action
  [Actions.CREATE_PRICE_INDEX_DATE_RANGE](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/indices/price-index-date-ranges', data)
      .then(() => {
        this.context.commit(Mutations.RESET_PRICE_INDEX_DATE_RANGES_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.DELETE_PRICE_INDEX_DATE_RANGE](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/indices/price-index-date-ranges', id)
      .then(() => {
        this.context.commit(Mutations.RESET_PRICE_INDEX_DATE_RANGES_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_PRICE_INDEX_DATE_RANGE]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/indices/price-index-date-ranges', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_PRICE_INDEX_DATE_RANGES_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_PRICE_INDEX_DATE_RANGE_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_PRICE_INDEX_DATE_RANGE_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_PRICE_INDEX_DATE_RANGE](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/indices/price-index-date-ranges', id);
  }

  @Action
  [Actions.GET_PRICE_INDEX_DATE_RANGES]() {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/indices/price-index-date-ranges', {
      params: { page: this.currentPage },
    })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_PRICE_INDEX_DATE_RANGES, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.RESET_PRICE_INDEX_DATE_RANGE_STORE]() {
    this.context.commit(Mutations.RESET_PRICE_INDEX_DATE_RANGES_LIST);
  }

  @Action
  [Actions.GET_INDEX_TYPES]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/indices/index-types');
  }

  @Action
  [Actions.GET_INDEX_INTERVALS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/indices/intervals');
  }
}
